import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useEntries, type EntriesStatus, type FeedTransport } from "@/hooks/useEntries";
import { useReferenceData, type ReferenceDataStatus } from "@/hooks/useReferenceData";
import { useVendorPaymentHistory } from "@/hooks/useVendorPaymentHistory";
import { useBalanceSheet } from "@/hooks/useBalanceSheet";
import { useProfitAndLoss } from "@/hooks/useProfitAndLoss";
import { usePayrollRoster } from "@/hooks/usePayrollRoster";
import { formatSignedDelta } from "@/lib/format";
import { derivePaymentAnomaly, type DerivedPaymentAnomaly } from "@/lib/anomaly";
import { deriveLiveDelta } from "@/lib/kpiDerivation";
import type { Account, Entry, PaymentMethod, PayrollEmployee, Vendor } from "@/api/types";

/**
 * ef-061-4 — the Home state the tiles SHARE, held once.
 *
 * Dashboard.tsx used to own all of this and render every card from it. Tiles are now independent registry
 * components (`lib/tileRegistry.ts`) that Home mounts in the saved order — and sometimes not at all — so the
 * live state cannot live in any one of them. It lives here, mounted by Dashboard whatever the layout, which is
 * what keeps the live-wall beats working with any subset of tiles on screen: a visitor entry still moves Cash on
 * hand (CashTile) and lands in the feed (ActivityTile) whether or not the other tile is mounted, because the SSE
 * feed, the ledger delta, the receipt-anomaly history and the payroll roster are each read ONCE, here.
 *
 * Every derivation below moved over from Dashboard.tsx unchanged — the reasoning for each (why the delta is
 * summed from `sessionArrivals` and not from the 25-row feed window, why the uncategorized count is
 * ledger-base-plus-arrivals, why the anomaly history has three sources) is recorded where it was first written
 * and has not changed; see git history of Dashboard.tsx before ef-061-4.
 */
export interface HomeShared {
  // ---- the live feed ----
  entries: Entry[];
  entriesStatus: EntriesStatus;
  entriesError: string | null;
  newEntryIds: ReadonlySet<string>;
  feedTransport: FeedTransport;
  // ---- reference data ----
  entityId: string | null;
  refStatus: ReferenceDataStatus;
  refError: string | null;
  vendorById: Map<string, Vendor>;
  accountById: Map<string, Account>;
  paymentMethodById: ReadonlyMap<string, PaymentMethod>;
  // ---- derived ----
  isLoading: boolean;
  hasError: boolean;
  /** Entries whose vendor is not in this farm's own list: proof of two farms on one screen. */
  crossTenantIds: number;
  /** Ledger cash plus the live delta; null until the ledger answers (never a plausible constant). */
  liveCashOnHand: number | null;
  liveNetFarmIncome: number | null;
  /** The signed delta chip, only while the arrival that produced it is still glowing in the feed. */
  kpiFlashDelta: string | null;
  anomalyByEntryId: ReadonlyMap<string, DerivedPaymentAnomaly>;
  uncategorizedReceiptCount: number;
  uncategorizedReceiptTotal: number;
  // ---- payroll roster ----
  employees: PayrollEmployee[] | null;
  nextPayrollGross: number | null;
}

const HomeSharedContext = createContext<HomeShared | null>(null);

export function HomeSharedProvider({ children }: { children: ReactNode }) {
  const {
    entries,
    status: entriesStatus,
    error: entriesError,
    newEntryIds,
    sessionArrivals,
    feedTransport,
    ledgerGeneration,
  } = useEntries();
  const { vendors, accounts, paymentMethods, entityId, status: refStatus, error: refError } = useReferenceData();

  const vendorById = useMemo(() => new Map<string, Vendor>(vendors.map((v) => [v.id, v] as const)), [vendors]);
  const accountById = useMemo(() => new Map<string, Account>(accounts.map((a) => [a.id, a] as const)), [accounts]);
  const paymentMethodById = useMemo(
    () => new Map<string, PaymentMethod>(paymentMethods.map((m) => [m.id, m] as const)),
    [paymentMethods],
  );

  // Every arrival since THIS tab loaded — a record, not a window (see the history above).
  const liveDelta = useMemo(() => deriveLiveDelta(sessionArrivals, accountById), [sessionArrivals, accountById]);
  const hasLiveDelta = liveDelta.count > 0;
  const deltaDollars = liveDelta.deltaCents / 100;

  const { sheet: balanceSheet } = useBalanceSheet(ledgerGeneration);
  const liveCashOnHand = balanceSheet === null ? null : balanceSheet.cashOnHand + deltaDollars;
  const { report: profitAndLoss } = useProfitAndLoss("cash", ledgerGeneration);
  const liveNetFarmIncome = profitAndLoss === null ? null : profitAndLoss.netIncome + deltaDollars;

  const payrollRoster = usePayrollRoster();
  const employees = payrollRoster.employees;
  const nextPayrollGross =
    employees === null ? null : employees.reduce((total, employee) => total + employee.grossWages, 0);

  // One shared clock with the row highlight: the chip shows only while the arrival still glows.
  const kpiFlashDelta = hasLiveDelta && newEntryIds.size > 0 ? formatSignedDelta(deltaDollars) : null;

  // Anomaly history: the ledger snapshot, the feed window, and everything that arrived this session.
  const vendorPaymentHistory = useVendorPaymentHistory(ledgerGeneration);
  const entriesForHistory = useMemo(() => [...entries, ...sessionArrivals], [entries, sessionArrivals]);
  const anomalyByEntryId = useMemo(() => {
    const map = new Map<string, DerivedPaymentAnomaly>();
    for (const entry of entries) {
      const anomaly = derivePaymentAnomaly(entry, entriesForHistory, paymentMethodById, vendorPaymentHistory);
      if (anomaly) map.set(entry.id, anomaly);
    }
    return map;
  }, [entries, entriesForHistory, paymentMethodById, vendorPaymentHistory]);

  const crossTenantIds = useMemo(() => {
    if (vendors.length === 0) return 0; // reference data still loading — not evidence of anything
    return entries.filter((e) => e.vendorId != null && !vendorById.has(e.vendorId)).length;
  }, [entries, vendors.length, vendorById]);

  // Needs-you: ledger base plus what has arrived since (same base-plus-delta shape as Cash on hand).
  const uncategorizedArrivals = useMemo(
    () => sessionArrivals.filter((entry) => entry.source === "Receipt" && !entry.accountId),
    [sessionArrivals],
  );
  const uncategorizedReceiptCount = (balanceSheet?.uncategorizedReceiptCount ?? 0) + uncategorizedArrivals.length;
  const uncategorizedReceiptTotal =
    (balanceSheet?.uncategorizedReceiptTotal ?? 0) + uncategorizedArrivals.reduce((sum, entry) => sum + entry.amount, 0);

  const isLoading = entriesStatus === "loading" || refStatus === "loading";
  const hasError = entriesStatus === "error" || refStatus === "error";

  const value = useMemo<HomeShared>(
    () => ({
      entries, entriesStatus, entriesError, newEntryIds, feedTransport,
      entityId, refStatus, refError, vendorById, accountById, paymentMethodById,
      isLoading, hasError, crossTenantIds, liveCashOnHand, liveNetFarmIncome, kpiFlashDelta,
      anomalyByEntryId, uncategorizedReceiptCount, uncategorizedReceiptTotal, employees, nextPayrollGross,
    }),
    [
      entries, entriesStatus, entriesError, newEntryIds, feedTransport,
      entityId, refStatus, refError, vendorById, accountById, paymentMethodById,
      isLoading, hasError, crossTenantIds, liveCashOnHand, liveNetFarmIncome, kpiFlashDelta,
      anomalyByEntryId, uncategorizedReceiptCount, uncategorizedReceiptTotal, employees, nextPayrollGross,
    ],
  );

  return <HomeSharedContext.Provider value={value}>{children}</HomeSharedContext.Provider>;
}

export function useHomeShared(): HomeShared {
  const value = useContext(HomeSharedContext);
  if (value === null) throw new Error("useHomeShared must be used inside <HomeSharedProvider> (Dashboard mounts it).");
  return value;
}
